import React from "react";
import { motion } from "framer-motion";

/* =====================================================================
   PARTNERSHIP COMMITMENT — Closing brand statement on the Dealer page.
   Full-width dark section with large, elegant typography. Minimal,
   spacious, business-focused. Sets up the Final CTA that follows.
   ===================================================================== */

export default function PartnershipCommitment() {
  return (
    <section
      data-testid="dealer-partnership-commitment"
      className="relative bg-liftile-matte-black py-28 md:py-40 lg:py-48 overflow-hidden"
    >
      {/* Soft ambient orange halo */}
      <div
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[820px] h-[820px] rounded-full pointer-events-none opacity-[0.10]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(60px)",
        }}
      />

      {/* Top + bottom thin orange accent lines */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px bg-liftile-orange/30" />
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px bg-liftile-orange/30" />

      <div className="container-x relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="heading-eyebrow mb-5">Our Commitment</div>

          {/* Subtle orange accent line above heading */}
          <div className="mx-auto w-14 h-px bg-liftile-orange mb-10" />

          <h2
            data-testid="dealer-partnership-heading"
            className="font-display uppercase text-liftile-cream leading-[0.98] tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-[4.25rem]"
          >
            A Partnership{" "}
            <br className="hidden sm:block" />
            Built on <span className="text-liftile-orange">Trust</span>
          </h2>

          <div className="mt-12 md:mt-14 space-y-6 md:space-y-7 font-body text-base md:text-lg lg:text-[1.18rem] text-liftile-cream/70 leading-[1.85] max-w-3xl mx-auto">
            <p>
              At LIFTILE, we believe successful dealerships are built through
              trust, transparency and long-term collaboration.
            </p>
            <p>
              Our goal is not simply to appoint dealers, but to build lasting
              business relationships that create value for our partners and
              customers alike.
            </p>
            <p>
              Together, we can deliver premium construction machinery backed by
              engineering excellence, dependable service and continuous support.
            </p>
          </div>

          {/* Closing tagline */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.25, ease: "easeOut" }}
            className="mt-16 md:mt-20"
            data-testid="dealer-partnership-tagline"
          >
            <div className="mx-auto w-14 h-px bg-liftile-orange mb-8" />
            <p className="font-display uppercase tracking-tight text-liftile-cream text-2xl sm:text-3xl md:text-4xl leading-[1.15]">
              Growing <span className="text-liftile-orange">Together.</span>
              <br />
              Building <span className="text-liftile-orange">Together.</span>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
